// pages/sections/ClientGroupModal.js
// Who else is in this client's Signer Group / Relationship Group. READ-ONLY: the
// Client Groups section owns the groups (with its step gates and sign-off freezes),
// so changes are made there. Loaded on demand from
// /api/diClientData/{period}?group=<ContIndex>.
//
// Members that aren't the viewer's own clients show everything except billing, which is
// withheld (admins see everything).

import React from 'react';
import { Link } from 'react-router-dom';
import Modal from '../../components/Modal';
import { usePeriod } from '../../hooks/usePeriod';
import { formatMoney, useFetchJson } from './clientDataUtil';

const money = n => formatMoney(n);

// The period phase whose release date opens the "Open Client Groups to change this" link.
// A period without a phase of this name (or no phases) leaves the link open.
const LINK_PHASE = 'Phase 2';

function GroupTable({ title, group }) {
  const withheldTip = 'Only shown for your own clients';
  return (
    <section className="cd-grp">
      <div className="cd-grp-head">
        <h4>{title}: {group.name} <span className="cd-grp-count">{group.size} clients</span></h4>
      </div>
      <div className="cd-grp-scroll">
      <table className="cd-bill cd-grp-table">
        <thead>
          <tr><th>Code</th><th>Client</th><th>Type</th><th>Partner</th><th>Manager</th><th className="cd-num">Billed (12 mo)</th></tr>
        </thead>
        <tbody>
          {group.members.map(m => (
            <tr key={m.clientCode} className={m.isThis ? 'cd-grp-this' : ''}>
              <td>{m.clientCode}</td>
              <td>
                {m.clientName}
                {m.isThis && <span className="cd-grp-tag">this client</span>}
                {m.isAnchor && !m.isThis && <span className="cd-grp-tag cd-grp-tag--anchor">anchor</span>}
                {m.you && !m.isThis && <span className="cd-grp-tag cd-grp-tag--you">yours</span>}
              </td>
              <td>{m.type || <span className="rt-cell-muted">—</span>}</td>
              <td>{m.partner || <span className="rt-cell-muted">—</span>}</td>
              <td>{m.manager || <span className="rt-cell-muted">—</span>}</td>
              <td className="cd-num" title={m.detail ? undefined : withheldTip}>
                {m.detail && m.billedTtm != null ? (m.billedTtm ? money(m.billedTtm) : '—') : <span className="rt-cell-muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
        {group.billedTotal != null && (
          <tfoot>
            <tr>
              <td colSpan={5}>{group.billedScope === 'all' ? 'Group total' : 'Total for your clients in this group'}</td>
              <td className="cd-num">{money(group.billedTotal)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      </div>
      {group.withheld > 0 && (
        <p className="cd-modal-note cd-bill-note">
          {group.withheld} client{group.withheld === 1 ? ' is' : 's are'} managed by someone else, so billing is hidden for {group.withheld === 1 ? 'it' : 'them'}.
        </p>
      )}
    </section>
  );
}

export default function ClientGroupModal({ period, row, onClose }) {
  const { data, error } = useFetchJson(`/api/diClientData/${period}?group=${encodeURIComponent(row.contIndex)}`);

  // "Open Client Groups to change this" opens with Phase 2. Unlike a section lock, admins are NOT
  // exempt here (`locked`, not `lockedForMe`) so the gate can be checked while signed in as an admin
  // or impersonating. Client Groups itself can stay hidden from Home — the link follows the PHASE's
  // release date, which comes from the period's phases (Admin → period → Phases), not from this file.
  // Admins can still reach Client Groups through Admin → Client Groups or the URL.
  const { phases } = usePeriod();
  const linkPhase = (phases || []).find(ph => ph.name === LINK_PHASE);
  const groupsLocked = !!linkPhase?.locked;
  const groupsLockMessage = linkPhase?.message || '';

  return (
    <Modal
      open
      onClose={onClose}
      className="cd-modal-groups"
      title={`Groups — ${row.clientCode} ${row.clientName}`}
      footer={
        <>
          {groupsLocked ? (
            <button type="button" className="cd-btn cd-btn--ghost" disabled title={groupsLockMessage}>
              Open Client Groups to change this — {groupsLockMessage}
            </button>
          ) : (
            <Link className="cd-btn cd-btn--ghost cd-btn-link" to={`/section/${period}/client-groups`} onClick={onClose}>Open Client Groups to change this</Link>
          )}
          <button type="button" className="cd-btn cd-btn--ghost" onClick={onClose}>Close</button>
        </>
      }
    >
      {!data && !error && <p className="cd-modal-note">Loading groups…</p>}
      {error && <p className="cd-warn">Couldn&apos;t load groups: {error}</p>}
      {data && !data.sg && !data.rg && (
        <p className="cd-modal-note">This client is standalone — it isn&apos;t grouped with any other client.</p>
      )}
      {data && data.rg && <GroupTable title="Relationship group" group={data.rg} />}
      {data && data.sg && <GroupTable title="Signer group" group={data.sg} />}
      {data && (data.sg || data.rg) && (
        <p className="cd-modal-note cd-bill-note">
          Groups are maintained in Client Relationship Groups; this view is read-only. A Signer Group is the clients that sign one engagement letter;
          a Relationship Group is the wider family/business cluster made of whole signer groups.
        </p>
      )}
    </Modal>
  );
}
